import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { openPresenceSocket } from "../lib/presence";
import { openWorkspaceSocket } from "../lib/workspaceFeed";
import ChildSwitcher from "../components/ChildSwitcher";
import AmbientDot from "../components/AmbientDot";
import CircleOfCalmOverlay from "../components/CircleOfCalmOverlay";
import AnnouncementThread from "../components/AnnouncementThread";
import HeroStateCard from "../components/HeroStateCard";
import StatusHistoryDrawer from "../components/StatusHistoryDrawer";
import { Sparkles, ChevronRight, LogOut, MapPin, Megaphone, BedDouble, Pin } from "lucide-react";

const STATE_PHRASE = {
  arrived: "has just arrived.",
  playing: "is playing.",
  resting: "is resting calmly.",
  eating: "is having a quiet snack.",
  learning: "is curiously exploring.",
  safe: "is safe, and held.",
};

const STATE_GERUND = {
  arrived: "Just arrived",
  playing: "Playing",
  resting: "Resting",
  eating: "Snacking",
  learning: "Exploring",
  safe: "Held safely",
};

// Mood-aware reassurance cadence (ms). Calm/restful states re-fetch the
// reassurance line more frequently to soothe parents; active states refresh
// less often to avoid notification noise.
const RESTFUL_STATES = new Set(["resting", "safe", "eating"]);
const REASSURANCE_INTERVAL_MS = {
  restful: 90 * 1000,  // 1.5 min
  active: 5 * 60 * 1000, // 5 min
};

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 5) return "Sleep softly";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 22) return "Good evening";
  return "A quiet night";
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}

export default function Home() {
  const { user, signOut } = useAuth();
  const { list, selected, loading, upsertSelected } = useChild();
  const navigate = useNavigate();
  const [reassurance, setReassurance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [livePulse, setLivePulse] = useState(false);
  const [calmAlert, setCalmAlert] = useState(null);
  const [copresent, setCopresent] = useState([]);

  useEffect(() => {
    if (!selected) {
      setReassurance(null);
      return;
    }
    let cancelled = false;
    const fetchOnce = () => {
      api
        .get(`/children/${selected.id}/reassurance`)
        .then((r) => {
          if (!cancelled) setReassurance(r.data);
        })
        .catch(() => {
          if (!cancelled) setReassurance(null);
        });
    };
    fetchOnce();
    setSummary(null);
    // Mood-aware cadence: more often when restful, less often when active.
    const state = selected.current_state;
    const cadence = RESTFUL_STATES.has(state)
      ? REASSURANCE_INTERVAL_MS.restful
      : REASSURANCE_INTERVAL_MS.active;
    const id = setInterval(fetchOnce, cadence);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [selected?.id, selected?.current_state]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Per-child presence WebSocket
  useEffect(() => {
    if (!selected) return;
    let alive = true;
    // Fetch initial copresence list (WS only pushes on join/leave events)
    api
      .get(`/children/${selected.id}/copresence`)
      .then((r) => {
        if (alive) setCopresent(r.data.users || []);
      })
      .catch(() => {});
    const close = openPresenceSocket(selected.id, (msg) => {
      if (!alive) return;
      if (msg.type === "state" && msg.child_id === selected.id) {
        upsertSelected({
          ...selected,
          current_state: msg.current_state,
          state_updated_at: msg.state_updated_at,
        });
        setLivePulse(true);
        setTimeout(() => setLivePulse(false), 1800);
      }
      if (msg.type === "zone" && msg.child_id === selected.id) {
        upsertSelected({
          ...selected,
          current_zone_id: msg.zone_id,
          current_zone_label: msg.zone_label,
          zone_updated_at: msg.zone_updated_at,
        });
      }
      if (msg.type === "copresence" && msg.child_id === selected.id) {
        setCopresent(msg.users || []);
      }
    });
    return () => {
      alive = false;
      if (close) close();
    };
  }, [selected, upsertSelected]);

  // Workspace WebSocket (for Circle of Calm broadcasts)
  useEffect(() => {
    if (!selected?.workspace_id) return;
    let alive = true;
    const close = openWorkspaceSocket(selected.workspace_id, (msg) => {
      if (!alive) return;
      if (msg.type === "circle_of_calm" && msg.entry) {
        setCalmAlert(msg.entry);
      }
    });
    // Also fetch the latest alert in case one was issued before this load
    api
      .get(`/workspaces/${selected.workspace_id}/circle-of-calm/latest`)
      .then((r) => {
        const issuedRecently =
          Date.now() - new Date(r.data.at).getTime() < 5 * 60 * 1000;
        if (issuedRecently) setCalmAlert(r.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (close) close();
    };
  }, [selected?.workspace_id]);

  const weave = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/children/${selected.id}/summary`);
      setSummary(r.data);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-serif italic text-[#6A6863]">
        a quiet moment…
      </div>
    );
  }

  if (!selected) {
    return <EmptyState role={user?.role} name={user?.name} hasList={list.length > 0} />;
  }

  return (
    <div className="min-h-screen">
      {/* Tiny breath-light topbar */}
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <ChildSwitcher />
          <div className="flex items-center gap-2 ml-auto">
            <Link
              to="/settings"
              data-testid="home-settings-link"
              className="text-xs tracking-widest uppercase text-[#6A6863] hover:text-[#2C2B29] transition-colors px-2 py-1"
            >
              Preferences
            </Link>
            <button
              data-testid="home-signout"
              onClick={() => {
                signOut();
                navigate("/");
              }}
              className="text-[#6A6863] hover:text-[#2C2B29] p-2 transition-colors"
              aria-label="Sign out"
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* The 10-second answer */}
      <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-6 pb-10 fade-up">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#525252] font-medium">
          {greetingByHour()}, {user?.name}
        </div>

        <HeroStateCard child={selected} reassurance={reassurance} />

        {/* Iteration 21 — calm "how today unfolded" status history drawer */}
        <StatusHistoryDrawer
          childId={selected.id}
          testid="home-status-history"
        />

        {selected.current_zone_label && (
          <div
            data-testid="home-zone-label"
            className="mt-4 inline-flex items-center gap-2.5 rounded-full bg-[#F2EFEA] border hairline px-4 py-2"
          >
            <MapPin size={12} strokeWidth={1.5} className="text-[#1F3A2A]" />
            <span className="text-sm text-[#171717] font-medium">
              Currently {selected.current_zone_label}
            </span>
          </div>
        )}

        {copresent.length > 0 && (
          <div
            data-testid="home-copresent"
            className="mt-3 inline-flex items-center gap-2.5 rounded-full bg-white border hairline px-4 py-2"
          >
            <span className="ambient-dot relative inline-block h-2 w-2 rounded-full bg-[#34C759]" />
            <span className="text-sm text-[#171717] font-medium italic">
              {copresent.length === 1
                ? `${copresent[0].name} is here too`
                : `${copresent.length} others are here too`}
            </span>
          </div>
        )}

        {/* Today's plan from the room */}
        {selected.workspace_id && <TodayPlanCard workspaceId={selected.workspace_id} />}

        {/* Today's attendance summary (parent-visible) */}
        {user?.role === "parent" && <AttendanceCard child={selected} />}

        {/* Big calm Pickup CTA */}
        {user?.role === "parent" && (
          <Link
            to="/pickup"
            data-testid="home-pickup-cta"
            className="mt-8 block rounded-3xl bg-[#1F3A2A] text-white px-7 py-7 transition-all hover:bg-[#132C17] hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(107,91,231,0.22)]"
          >
            <div className="flex items-center justify-between gap-4">
              <span>
                <span className="text-[11px] tracking-[0.22em] uppercase text-[#EAE5EF]">
                  Pickup mode
                </span>
                <span className="block mt-2 font-serif text-2xl sm:text-3xl">
                  Heading to {selected.name}?
                </span>
                <span className="block mt-1 text-sm text-white/75 font-medium">
                  Let staff know with one calm tap.
                </span>
              </span>
              <ChevronRight size={22} strokeWidth={1.5} />
            </div>
          </Link>
        )}

        {/* Single CTA — one action per emotional moment */}
        <div className="mt-8 flex flex-col gap-3">
          {summary ? (
            <article
              data-testid="daily-summary-card"
              className="rounded-3xl border hairline bg-[#F2EFEA]/60 p-7 sm:p-9"
            >
              <div className="flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.5} className="text-[#8A9984]" />
                <span className="eyebrow">Today, gently woven</span>
              </div>
              <p
                data-testid="daily-summary-text"
                className="mt-5 font-serif text-2xl sm:text-3xl italic text-[#2C2B29] leading-snug"
              >
                {summary.summary}
              </p>
            </article>
          ) : (
            <button
              type="button"
              onClick={weave}
              disabled={busy}
              data-testid="home-weave-btn"
              className="rounded-3xl border hairline bg-white px-7 py-6 text-left transition-colors duration-500 hover:bg-[#F2EFEA] flex items-center justify-between gap-4"
            >
              <span>
                <span className="flex items-center gap-2">
                  <Sparkles size={14} strokeWidth={1.5} className="text-[#8A9984]" />
                  <span className="eyebrow">Today, in one calm paragraph</span>
                </span>
                <span className="block mt-2 font-serif text-xl sm:text-2xl text-[#2C2B29]">
                  {busy ? "Listening…" : "Weave today gently"}
                </span>
              </span>
              <ChevronRight size={20} strokeWidth={1.5} className="text-[#6A6863]" />
            </button>
          )}

          <Link
            to="/moments"
            data-testid="home-link-moments"
            className="rounded-3xl border hairline bg-white px-7 py-5 transition-colors duration-500 hover:bg-[#F2EFEA] flex items-center justify-between gap-4"
          >
            <span>
              <span className="eyebrow">Today&rsquo;s moments</span>
              <span className="block mt-1 font-serif text-lg text-[#2C2B29]">
                Read the small things
              </span>
            </span>
            <ChevronRight size={18} strokeWidth={1.5} className="text-[#6A6863]" />
          </Link>

          <Link
            to="/messages"
            data-testid="home-link-letters"
            className="rounded-3xl border hairline bg-white px-7 py-5 transition-colors duration-500 hover:bg-[#F2EFEA] flex items-center justify-between gap-4"
          >
            <span>
              <span className="eyebrow">Letters</span>
              <span className="block mt-1 font-serif text-lg text-[#2C2B29]">
                Write softly
              </span>
            </span>
            <ChevronRight size={18} strokeWidth={1.5} className="text-[#6A6863]" />
          </Link>

          {selected && <AbsenceReportButton childObj={selected} />}
        </div>

        {selected && <PinnedAnnouncements />}
        {selected && <RecentlyPanel childObj={selected} />}
      </main>

      <CircleOfCalmOverlay alert={calmAlert} onClose={() => setCalmAlert(null)} />
    </div>
  );
}

function moodFromState(state) {
  return {
    resting: "settled",
    playing: "playful",
    learning: "curious",
    eating: "content",
    arrived: "easing in",
    safe: "calm",
  }[state] || "calm";
}

function QuickStat({ label, value }) {
  return (
    <div
      data-testid={`home-stat-${label.toLowerCase()}`}
      className="rounded-2xl bg-[#FBF9F6] border hairline px-4 py-3"
    >
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#6A6863]">
        {label}
      </div>
      <div className="mt-1 font-serif text-base text-[#2C2B29] capitalize">
        {value}
      </div>
    </div>
  );
}

function EmptyState({ role, name, hasList }) {
  if (role === "admin") {
    return (
      <div className="min-h-screen px-6 sm:px-10 pt-24 max-w-2xl mx-auto fade-up">
        <span className="eyebrow">Welcome</span>
        <h1 className="mt-6 font-serif text-5xl leading-tight tracking-tight">
          Hello, <em className="italic text-[#8A9984]">{name}.</em>
        </h1>
        <p className="mt-8 text-lg text-[#6A6863] font-light">
          Your space lives under <strong className="font-medium text-[#2C2B29]">Center</strong> in the calm bar below.
        </p>
        <Link
          to="/workspace"
          data-testid="home-admin-go"
          className="btn-primary mt-12 inline-flex"
        >
          Open the Center
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 sm:px-10 pt-24 max-w-2xl mx-auto fade-up">
      <span className="eyebrow">A quiet beginning</span>
      <h1 className="mt-6 font-serif text-5xl leading-tight tracking-tight">
        Welcome, <em className="italic text-[#8A9984]">{name}.</em>
      </h1>
      <p className="mt-8 text-lg text-[#6A6863] font-light">
        {role === "parent"
          ? "When you're ready, add a child to begin. Nothing happens until you say so."
          : hasList
          ? "Pick a child below to share."
          : "When a parent connects you to their child, you'll find them here."}
      </p>
      {role === "parent" && (
        <Link
          to="/child"
          data-testid="home-empty-add-child"
          className="btn-primary mt-12 inline-flex"
        >
          Add a child
        </Link>
      )}
    </div>
  );
}



/** A soft thread of recent calm moments. NO badges, NO toasts, NO counters.
 *  Just a quiet list that updates as new whispers arrive over WebSocket. */
function RecentlyPanel({ childObj }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initial load + soft poll every 30s as a gentle backup to the WS stream.
  useEffect(() => {
    if (!childObj?.id) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/users/me/notifications", {
          params: { child_id: childObj.id, limit: 8 },
        });
        if (alive) setItems(r.data || []);
      } catch {
        // gentle silence — never alarm the parent
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 30 * 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [childObj?.id]);

  // Live: re-load softly on any presence event for this child.
  useEffect(() => {
    if (!childObj?.id) return;
    const close = openPresenceSocket(childObj.id, () => {
      api
        .get("/users/me/notifications", {
          params: { child_id: childObj.id, limit: 8 },
        })
        .then((r) => setItems(r.data || []))
        .catch(() => {});
    });
    return () => close && close();
  }, [childObj?.id]);

  return (
    <section className="mt-14" data-testid="home-recently-panel">
      <span className="eyebrow">Recently, in {childObj.name}&rsquo;s room</span>
      {loading ? (
        <p className="mt-5 font-serif italic text-[#6A6863]">A quiet moment…</p>
      ) : items.length === 0 ? (
        <p
          data-testid="home-recently-empty"
          className="mt-5 font-serif italic text-[#6A6863]"
        >
          All is well. Nothing new to share.
        </p>
      ) : (
        <ol
          data-testid="home-recently-list"
          className="mt-6 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
        >
          {items.map((n) => (
            <li
              key={n.id}
              data-testid={`home-recently-item-${n.id}`}
              className="bg-[#FBF9F6] p-5"
            >
              <p className="font-serif text-lg text-[#2C2B29] leading-snug">
                {n.text}
              </p>
              <p className="mt-1 text-xs text-[#6A6863]">{timeAgo(n.created_at)}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}



/** A soft, dismissible button for parents to report an absence/sickness. */
function AbsenceReportButton({ childObj }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, kind: "absent", note: "" });
  const [busy, setBusy] = useState(false);

  if (!user || user.role !== "parent") return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/children/${childObj.id}/absences`, {
        date: form.date,
        kind: form.kind,
        note: form.note.trim() || null,
      });
      setForm({ date: today, kind: "absent", note: "" });
      setOpen(false);
      // Soft toast from sonner — single tap, no badge.
      const { toast } = await import("sonner");
      toast(`${childObj.name} is gently noted as ${form.kind} today.`);
    } catch (err) {
      const { toast } = await import("sonner");
      toast(err.response?.data?.detail || "Couldn't save right now.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="home-absence-open"
        className="rounded-3xl border hairline bg-white px-7 py-5 transition-colors duration-500 hover:bg-[#F2EFEA] flex items-center justify-between gap-4"
      >
        <span className="text-left">
          <span className="eyebrow">A gentle note</span>
          <span className="block mt-1 font-serif text-lg text-[#2C2B29]">
            Let the center know
          </span>
        </span>
        <BedDouble size={18} strokeWidth={1.5} className="text-[#8A9984]" />
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="home-absence-form"
      className="rounded-3xl border hairline bg-[#F2EFEA]/60 p-7"
    >
      <div className="flex items-center gap-2">
        <BedDouble size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">A gentle note for the center</span>
      </div>
      <p className="mt-3 font-serif italic text-[#2C2B29] text-lg">
        {childObj.name} won&rsquo;t be in today.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="eyebrow">Date</label>
          <input
            type="date"
            required
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="home-absence-date"
            className="orbit-input mt-2"
          />
        </div>
        <div>
          <label className="eyebrow">Reason</label>
          <select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            data-testid="home-absence-kind"
            className="orbit-input mt-2 bg-transparent"
          >
            <option value="absent">Absent</option>
            <option value="sick">Resting (unwell)</option>
            <option value="vacation">On holiday</option>
            <option value="day_off">A quiet day off</option>
            <option value="appointment">Appointment</option>
            <option value="late_arrival">Arriving a little later</option>
            <option value="early_pickup">Heading home a little early</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow">A note (optional)</label>
          <input
            type="text"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="back tomorrow, just a quiet morning…"
            data-testid="home-absence-note"
            className="orbit-input mt-2"
          />
        </div>
      </div>

      <div className="mt-7 flex flex-wrap gap-3 justify-end">
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="home-absence-cancel"
          className="btn-ghost !py-2 !px-5 !text-xs"
        >
          Not now
        </button>
        <button
          type="submit"
          disabled={busy}
          data-testid="home-absence-submit"
          className="btn-primary !py-2 !px-5 !text-xs"
        >
          {busy ? "Sharing…" : "Share gently"}
        </button>
      </div>
    </form>
  );
}

/** Pinned announcement cards on the parent's Home. Dismissible. */
function PinnedAnnouncements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openThread, setOpenThread] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/announcements/pinned");
      setItems(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dismiss = async (id) => {
    try {
      await api.post(`/announcements/${id}/dismiss`);
      setItems((prev) => prev.filter((a) => a.id !== id));
    } catch {
      // quiet
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <section
      data-testid="home-pinned-announcements"
      className="mt-12 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
    >
      {items.map((a) => {
        const canReply = a.reply_mode === "open" || a.reply_mode === "quiet";
        const replyLabel =
          a.reply_mode === "quiet" ? "Reply quietly" : "Reply";
        return (
          <article
            key={a.id}
            data-testid={`home-announcement-${a.id}`}
            data-reply-mode={a.reply_mode || "broadcast"}
            className="bg-white p-7 flex items-start gap-5"
          >
            <span
              className="mt-1 h-9 w-9 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "#FFB800" }}
              aria-hidden
            >
              <Pin size={16} strokeWidth={1.5} className="text-[#1A1B2E]" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="eyebrow">{a.workspace_name || "Your center"}</div>
              <h3 className="mt-2 font-serif text-xl text-[#2C2B29]">{a.title}</h3>
              <p className="mt-2 text-base text-[#6A6863] font-light leading-snug whitespace-pre-line">
                {a.body}
              </p>
              <p className="mt-3 text-xs text-[#6A6863]">
                from {a.created_by_name} · {timeAgo(a.created_at)}
                {a.target_group_name && (
                  <span> · {a.target_group_name}</span>
                )}
                {a.reply_count > 0 && (
                  <span data-testid={`home-announcement-${a.id}-replies`}>
                    {" · "}
                    {a.reply_count} {a.reply_count === 1 ? "reply" : "replies"}
                  </span>
                )}
              </p>
              {canReply && (
                <button
                  type="button"
                  onClick={() => setOpenThread(a)}
                  data-testid={`home-announcement-${a.id}-reply`}
                  className="btn-ghost mt-4 !py-2 !px-4 !text-xs"
                >
                  {replyLabel}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(a.id)}
              data-testid={`home-announcement-dismiss-${a.id}`}
              aria-label="Quietly dismiss"
              className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-1"
            >
              <span aria-hidden>×</span>
            </button>
          </article>
        );
      })}
      {openThread && (
        <AnnouncementThread
          announcement={openThread}
          currentUser={user}
          onClose={async () => {
            setOpenThread(null);
            await load();
          }}
        />
      )}
    </section>
  );
}



/** Today's plan card — small, calm summary of the workspace's daily flow. */
function TodayPlanCard({ workspaceId }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get(`/workspaces/${workspaceId}/today-plan`)
      .then((r) => {
        if (alive) setPlan(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [workspaceId]);

  if (loading || !plan || !plan.items || plan.items.length === 0) return null;

  return (
    <section
      data-testid="home-today-plan"
      className="mt-8 rounded-3xl border hairline bg-white p-7"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="eyebrow">Today, in {plan.workspace_name || "the room"}</span>
        {plan.updated_by_name && (
          <span className="text-xs text-[#6A6863]">by {plan.updated_by_name}</span>
        )}
      </div>
      <ol
        data-testid="home-today-plan-list"
        className="mt-5 space-y-3"
      >
        {plan.items.map((item, idx) => (
          <li
            key={idx}
            data-testid={`home-plan-item-${idx}`}
            className="flex items-start gap-4"
          >
            <span className="font-mono text-xs text-[#1A1B2E] tracking-wider w-14 pt-1 shrink-0">
              {item.time}
            </span>
            <span className="font-serif text-lg text-[#2C2B29] leading-snug">
              {item.label}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}


/** Today's attendance summary — calm, parent-visible.
 *  Surfaces "signed in at 8:42 AM by Mira" or "Not yet signed in today."
 *  After sign-out, shows who picked the child up. Calm, never surveillance.
 */
function AttendanceCard({ child }) {
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!child?.id) return;
    let alive = true;
    const startedAt = Date.now();
    const MIN_SHIMMER_MS = 280;
    api
      .get(`/children/${child.id}/attendance?days=1`)
      .then((r) => {
        const data = (r.data || [])[0] || null;
        const elapsed = Date.now() - startedAt;
        const wait = Math.max(0, MIN_SHIMMER_MS - elapsed);
        setTimeout(() => {
          if (alive) {
            setToday(data);
            setLoading(false);
          }
        }, wait);
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [child?.id]);

  if (loading) {
    return (
      <section
        data-testid="home-attendance-card-skeleton"
        aria-busy="true"
        className="mt-6 rounded-3xl border hairline bg-white p-7"
      >
        <span className="eyebrow">Today's attendance</span>
        <div className="mt-4 h-5 w-3/4 alouvya-skeleton" />
        <div className="mt-2 h-3 w-1/2 alouvya-skeleton" />
      </section>
    );
  }
  if (!today) return null;

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  let line = null;
  let testidSuffix = today.status;
  if (today.status === "departed") {
    line = (
      <>
        Signed out at <span className="text-[#2C2B29]">{fmt(today.departed_at)}</span>
        {today.departed_to_name ? (
          <>
            {" "}
            with <span className="text-[#2C2B29]">{today.departed_to_name}</span>
          </>
        ) : null}
        .
      </>
    );
  } else if (today.status === "present") {
    line = (
      <>
        Signed in at <span className="text-[#2C2B29]">{fmt(today.arrived_at)}</span>
        {today.arrived_by_name ? (
          <>
            {" "}
            by <span className="text-[#2C2B29]">{today.arrived_by_name}</span>
          </>
        ) : null}
        .
      </>
    );
  } else if (today.status === "absent_today") {
    line = <>Resting at home today.</>;
  } else {
    line = <>Not yet signed in today.</>;
  }

  return (
    <section
      data-testid="home-attendance-card"
      data-status={today.status}
      className="mt-6 rounded-3xl border hairline bg-white p-7"
    >
      <span className="eyebrow">Today's attendance</span>
      <p
        data-testid={`home-attendance-line-${testidSuffix}`}
        className="mt-3 font-serif text-xl text-[#6A6863] leading-snug"
      >
        {line}
      </p>
      {today.arrived_note && today.status === "present" && (
        <p className="mt-2 text-sm text-[#6A6863] italic font-serif">
          "{today.arrived_note}"
        </p>
      )}
      {today.departed_note && today.status === "departed" && (
        <p className="mt-2 text-sm text-[#6A6863] italic font-serif">
          "{today.departed_note}"
        </p>
      )}
    </section>
  );
}
